import { useEffect, useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { getMyMeetingRole } from 'flow/api-clients/participant-identity-client';
import {
    selectIsUserLoggedIn,
    selectLobbyMeetingCode,
} from 'flow/selectors/meeting-selectors';
import { MeetingRole } from 'flow/utils/meeting-constants';

/**
 * Answers already given, by meeting code. How home classifies someone for a
 * given meeting does not change while the tab is open, so the lobby should not
 * go back to a skeleton every time it is mounted — which it is on every visit,
 * including the one right after hanging up. Keyed by meeting code, so an answer
 * never describes a different meeting than the one being looked at.
 *
 * An answer naming no role is not kept: that is what a failed asking looks
 * like, and the next visit deserves a fresh attempt rather than an inherited
 * failure.
 */
const roleByMeetingCode = new Map<string, string>();

/** Forgets every answer. Exists for tests, which must not inherit each other's. */
export const resetViewerMeetingRoleCache = (): void => {
    roleByMeetingCode.clear();
};

export interface ViewerMeetingRole {
    /** Whether this viewer will be held at the door rather than walking in. */
    viewerMustAskToJoin: boolean;
    /** Whether that is home's answer rather than the cautious default. */
    viewerMustAskToJoinIsKnown: boolean;
    /**
     * The role home named, when it named one. Reported on the button as a
     * data-test attribute, so an autotest that trips over the label can say
     * what the lobby had been told rather than only what it showed.
     */
    meetingRole: string | null;
    /** Whether the answer is still on its way, and the button should hold off. */
    meetingRoleIsPending: boolean;
}

/**
 * How home classifies this viewer for the lobby's meeting (VID-76), which is
 * what the join button has to know before it can say whether pressing it joins
 * or asks.
 *
 * Called in exactly one place, MeetingLobby, which passes the answer down to
 * the button. Keep it that way: two callers are two questions about the same
 * person and the same meeting, and nothing makes the answers agree — home has
 * been seen giving two of them different roles (VID-430). A lobby whose button
 * and whose join were told different things is a button that says one and does
 * the other.
 *
 * Kept here rather than in the store: one button reads it, it belongs to the
 * lobby you are looking at, and scoping it to the component that cares means a
 * reply for a meeting you have since left is dropped by the effect's own
 * cleanup instead of having to be recognised and discarded.
 *
 * Only a colleague walks in, so anyone home has not named as one asks. That is
 * also the right answer for a guest, who is unauthenticated and never has a
 * role to read, and the safe way round for anything unrecognised: promising to
 * ask and then walking straight in is a far better surprise than promising
 * entry and being stopped.
 *
 * The wait is bounded in getMyMeetingRole, which gives up and answers null. The
 * button stands in as a skeleton until then, so an answer that never came would
 * otherwise be a lobby with no way into the meeting at all.
 */
const useViewerMeetingRole = (): ViewerMeetingRole => {
    const meetingCode = useAppSelector(selectLobbyMeetingCode);
    const isLoggedIn = useAppSelector(selectIsUserLoggedIn);

    const [meetingRole, setMeetingRole] = useState<string | null>(
        () => (meetingCode && roleByMeetingCode.get(meetingCode)) || null,
    );
    const [meetingRoleIsPending, setMeetingRoleIsPending] = useState(false);

    useEffect(() => {
        // A guest has nothing to ask and nothing to wait for: no role reads as
        // external, which is what they are.
        if (!meetingCode || !isLoggedIn) {
            setMeetingRole(null);
            setMeetingRoleIsPending(false);
            return undefined;
        }

        const known = roleByMeetingCode.get(meetingCode);
        if (known) {
            setMeetingRole(known);
            setMeetingRoleIsPending(false);
            return undefined;
        }

        let isCurrent = true;
        setMeetingRole(null);
        setMeetingRoleIsPending(true);

        getMyMeetingRole(meetingCode)
            .then((role) => {
                if (role) {
                    roleByMeetingCode.set(meetingCode, role);
                }
                if (isCurrent) {
                    setMeetingRole(role);
                }
            })
            .catch(() => {
                // No role, rather than an answer of "no role" — either way the
                // fallback below has us asking to join.
            })
            .finally(() => {
                if (isCurrent) {
                    setMeetingRoleIsPending(false);
                }
            });

        // A reply for a meeting we have since left describes that meeting, not
        // this one.
        return () => {
            isCurrent = false;
        };
    }, [meetingCode, isLoggedIn]);

    const viewerMustAskToJoin =
        meetingRole !== MeetingRole.INTERNAL_MEMBER &&
        meetingRole !== MeetingRole.INTERNAL_NONMEMBER;

    return {
        viewerMustAskToJoin,
        // Whether that is known rather than assumed. A guest is external by
        // definition and needs no lookup; for anyone signed in it is only known
        // once home has answered. The difference matters where the answer drives
        // state rather than wording: labelling a button "Ask to join" on a guess
        // costs nothing if the guess is wrong, but starting the join already
        // waiting would strand a colleague in a wait nobody is going to end
        // whenever the lookup is unavailable.
        viewerMustAskToJoinIsKnown: !isLoggedIn || meetingRole !== null,
        meetingRole,
        meetingRoleIsPending,
    };
};

export default useViewerMeetingRole;
